import React, { useState, useRef } from "react";
import { CSpinner } from "@coreui/react-pro";
import { useNavigate } from "react-router-dom";
import { createShorts } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { CTooltip } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import {cilInfo} from '@coreui/icons';

const CreateShorts = () => {
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const initialValues = {
    title: "",
    description: "",
    file: null,
    status: "",
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    status: Yup.string()
      .required("Status is required")
      .notOneOf([""], "Please select a valid status"),
    file: Yup.mixed()
      .required("Video is required")
      .test("fileSize", "File size must not exceed 2MB.", (value) => {
        return value ? value.size <= 2 * 1024 * 1024 : false;
      })
      .test(
        "fileFormat",
        "Unsupported video format. Only MP4 or MOV allowed.",
        (value) => {
          return value
            ? ["video/mp4", "video/quicktime"].includes(value.type)
            : true;
        }
      )
      .test("fileSize", "File size must not exceed 2MB.", (value) => {
        return value ? value.size <= 2 * 1024 * 1024 : false;
      }),
  });

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      // Create FormData
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("status", values.status);

      const file = values.file;
      if (file) {
        formData.append("video", file); // Directly append the video file
      } else {
        console.error("No video file selected.");
        toast.error("No video file selected.");
        return;
      }

      try {
        // Send the FormData to the backend
        await createShorts(formData); // Adjust backend utility to accept FormData
        toast.success("Short created successfully!");
        navigate("/shorts");
      } catch (error) {
        console.error("Failed to create short:", error);
        toast.error(
          error.message || "Failed to create short. Please try again."
        );
        setErrors({ submit: "Failed to create short", ...error.fieldErrors });
        Object.keys(values).forEach((field) =>
          setFieldTouched(field, true, false)
        );
      } finally {
        setSubmitting(false);
        setIsSubmittingForm(false);
      }
    } catch (error) {
      console.error("Failed to create short:", error);
      toast.error(error.message || "Failed to create short. Please try again.");
      setErrors({ submit: "Failed to create short", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, isSubmitting, errors, touched }) => (
          <Form>
            {isSubmittingForm && (
              <div className="form-overlay">
                <CSpinner color="primary" />
              </div>
            )}
            <div className="card border-light">
              <div className="card-body">
                {errors.submit && (
                  <div className="alert alert-danger">{errors.submit}</div>
                )}
                {/* Form fields */}
                <div className="row">
                  {/* Title */}
                  <div className="col-md-6 mb-3">
                    <label htmlFor="title" className="form-label">
                      <span className="text-danger">*</span> Title
                    </label>{" "}
                    <Field
                      name="title"
                      as="input"
                      type="text"
                      placeholder="Enter Title"
                      className={`form-control ${
                        touched.title && errors.title ? "is-invalid" : ""
                      }`}
                    />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                  {/* Description */}
                  <div className="col-md-6 mb-3">
                    <label htmlFor="description" className="form-label">
                      <span className="text-danger">*</span> Description
                    </label>{" "}
                    <Field
                      name="description"
                      as="input"
                      type="text"
                      placeholder="Enter Description"
                      className={`form-control ${
                        touched.description && errors.description
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="description"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                </div>
                <div className="row">
                  {/* Video */}
                  <div className="col-md-6 mb-3">
                    <label htmlFor="file" className="form-label">
                      <span className="text-danger">*</span> Upload Video
                    </label>
                    <CTooltip
                      content={
                        <>
                        <ul>
                        <li>Max file size: 2MB</li>
                        <li>Supported formats: MP4, MOV</li>
                          <li>
                            Recommended dimension: <br />
                            1920 (h) x 1080 (w)
                          </li>
                        </ul>
                      </>
                      }                      
                      placement="right"
                    >
                      <CIcon icon={cilInfo} className="mx-2 text-danger"></CIcon>
                    </CTooltip>
                    <input
                      id="file"
                      name="file"
                      type="file"
                      ref={fileInputRef}
                      className={`form-control ${
                        touched.file && errors.file ? "is-invalid" : ""
                      }`}
                      accept="video/mp4,video/quicktime" // Restrict video picker to MP4 and MOV
                      onChange={(event) => {
                        const file = event.currentTarget.files[0];
                        setFieldValue("file", file);
                      }}
                      // onChange={(event) => {
                      //   const file = event.currentTarget.files[0];
                      //   if (file) {
                      //     const maxSize = 2 * 1024 * 1024; // 2MB in bytes
                      //     if (file.size > maxSize) {
                      //       toast.error("File size must not exceed 2MB.");
                      //       setFieldValue("file", null); // Reset the file input in Formik's state
                      //       if (fileInputRef.current) {
                      //         fileInputRef.current.value = "";
                      //       }
                      //     } else {
                      //       toast.success("File selected succefully!");
                      //       setFieldValue("file", file); // Update Formik's state with valid file
                      //     }
                      //   }
                      // }}
                    />
                    <ErrorMessage
                      name="file"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>
                  {/* Status */}
                  <div className="col-md-6 mb-3">
                    <label htmlFor="status" className="form-label">
                      <span className="text-danger">*</span> Status
                    </label>{" "}
                    <div className="input-group">
                      <Field
                        name="status"
                        as="select"
                        className={`form-select ${
                          touched.status && errors.status ? "is-invalid" : ""
                        }`}
                      >
                        <option value="">Select a status</option>
                        <option key="Pending" value="Pending">
                          Pending Review
                        </option>
                        <option key="Approved" value="Approved">
                          Approved
                        </option>
                        <option key="Rejected" value="Rejected">
                          Rejected
                        </option>
                      </Field>
                      <ErrorMessage
                        name="status"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                </div>
                <div className=" d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn me-3 ansa-button-secondary"
                    onClick={() => navigate("/shorts")}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn ansa-button-primary"
                    disabled={isSubmitting}
                  >
                    Create Short
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateShorts;
